import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ArrowLeftRight } from "lucide-react";
import { useApp } from "@/contexts/AppContext";
import { useUserAuth } from "@/contexts/UserAuthContext";
import { useActiveCountries } from "@/hooks/useAppData";
import { useCashbackTransactions, useUserProfile } from "@/hooks/useCashback";
import {
  getCurrencySymbolForCountry,
  getCurrencySymbolForCurrencyCode,
} from "@/utils/formatCurrency";
import { resolveUserCountryCode } from "@/utils/resolveUserCountryCode";
import { CashbackWithdrawTransfer } from "@/components/account/CashbackWithdrawTransfer";

export default function CashbackWalletManager() {
  const navigate = useNavigate();
  const { user } = useUserAuth();
  const { selectedCountry } = useApp();
  const { countries } = useActiveCountries();
  const { transactions } = useCashbackTransactions(user?.uid ?? null);
  const { profile } = useUserProfile(user?.uid ?? null);

  const summary = profile?.cashbackSummary;
  const confirmedNetFromTransactions = useMemo(
    () =>
      transactions.reduce((sum, tx) => {
        if (tx.status !== "confirmed" && tx.status !== "paid") return sum;
        const amount = Number(tx.cashbackAmount) || 0;
        const signed =
          tx.source === "admin_adjustment" && tx.adjustmentSign === -1
            ? -amount
            : amount;
        return sum + signed;
      }, 0),
    [transactions]
  );
  const effectiveAvailable = Math.max(
    summary?.availableBalance ?? 0,
    confirmedNetFromTransactions
  );

  const currencySymbol = useMemo(() => {
    if (summary?.currencyCode) {
      return getCurrencySymbolForCurrencyCode(summary.currencyCode);
    }
    const code = resolveUserCountryCode(profile, selectedCountry, countries);
    return getCurrencySymbolForCountry(code);
  }, [countries, profile, selectedCountry, summary?.currencyCode]);

  return (
    <div className="flex flex-col flex-1 min-h-0 bg-background">
      <div className="flex items-center justify-between gap-3 px-4 pt-12 pb-5 shrink-0">
        <h1 className="text-xl font-bold text-foreground">إدارة الرصيد</h1>
        <button
          onClick={() => navigate(-1)}
          className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center shrink-0 active:opacity-90 transition-opacity ring-2 ring-white/20 shadow-sm"
        >
          <ChevronLeft className="w-5 h-5" strokeWidth={2.5} />
        </button>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto pb-6">
        <div className="mx-4 mb-4 rounded-2xl bg-card border border-border px-4 py-3 flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-primary/15 flex items-center justify-center shrink-0">
            <ArrowLeftRight className="w-5 h-5 text-primary" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-xs text-muted-foreground">الرصيد المتاح لإدارة العمليات</p>
            <p className="text-lg font-bold text-foreground tabular-nums">
              {effectiveAvailable.toFixed(2)} {currencySymbol}
            </p>
          </div>
        </div>

        {user ? (
          <CashbackWithdrawTransfer
            availableBalance={effectiveAvailable}
            currencySymbol={currencySymbol}
            userId={user.uid}
          />
        ) : null}
      </div>
    </div>
  );
}
